import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Quill from "quill";
import {useAuth} from "hooks/useAuth";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {SelectChangeEvent} from "@mui/material/Select";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import Loading from "shared/ui/Loading";
import ErrorsList from "shared/ui/ErrorsList";
import FormField from "shared/form/FormField";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import GenericButton from "shared/ui/GenericButton";
import {useServerErrors} from "../../../../hooks/useServerErrors";
import {APIPostCustomItemsGrades, APIPostCustomItemsSubjects} from "../api/customSubcategories";
import {APIGetCustomItem, APIPostCustomItem, APIPostCustomSubcategories, APIUpdateCustomItem} from "../api/customItem";
import CustomTextEditor from "../../../../shared/textEditor/CustomTextEditor";
import CustomButton from "../../../../shared/ui/CustomButton";
import TestItemCard from "../../../../shared/ui/TestItemCard";

export interface IFormCustomItemsEdit {
    subject: string;
    grade: string;
    subcategory: string;
    title: string,
    right_answer: string;
    q_data: string;
    q_obs: string;
    q_1: string;
    q_2: string;
    q_3: string;
    q_4: string;
    duration: number | undefined;
}

const schema = yup
    .object({})
    .required();

const Delta = Quill.import('delta');

const INITIAL_ITEM = {
    id: "",
    subject: { id: "" },
    subcategory: { id: "" },
    item_type: "",
    title: "",
    right_answer: "",
    is_active: true,
    q_data: "",
    q_obs: "",
    q_1: "",
    q_2: "",
    q_3: "",
    q_4: "",
    grade: { id: "" },
    duration: undefined,
};

const CustomTestItem = () => {
    const {id = "", action} = useParams();
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [item, setItem] = useState<any>(INITIAL_ITEM);
    const [subcategories, setSubcategories] = useState<any>([]);
    const [subcategory, setSubcategory] = useState<any>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [subjects, setSubjects] = useState<any[]>([]);
    const [subject, setSubject] = useState("");
    const [grades, setGrades] = useState<any[]>([]);
    const [grade, setGrade] = useState("");
    const [ready, setReady] = useState<boolean>(false);
    const [qData, setQData] = useState("");
    const [qObs, setQObs] = useState("");
    const [q1, setQ1] = useState("");
    const [q2, setQ2] = useState("");
    const [q3, setQ3] = useState("");
    const [q4, setQ4] = useState("");

    const quillRefQData = useRef();
    const quillRefQObs = useRef();
    const quillRefQ1 = useRef();
    const quillRefQ2 = useRef();
    const quillRefQ3 = useRef();
    const quillRefQ4 = useRef();

    const methods = useForm<IFormCustomItemsEdit>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormCustomItemsEdit>(methods.setError);

    useEffect(() => {

        const fetchSubjectsAndGrades = async () => {
            try {
                const subjectsResponse = await APIPostCustomItemsSubjects();
                if(!subjectsResponse.data.success) update("error", {show: true, code: "A002"});
                setSubjects(subjectsResponse.data.data.data.items);

                const gradesResponse = await APIPostCustomItemsGrades();
                if(!gradesResponse.data.success) update("error", {show: true, code: "A002"});
                setGrades(gradesResponse.data.data.data.items);

                setReady(true);
            } catch (e) {update("error", {show: true, code: "A001"})}
        };

        fetchSubjectsAndGrades();
    }, []);

    useEffect(() => {

        const initNewItemIfAddRoute = () => {
            const locationArray = location.pathname.split("/");
            const isAddRoute = locationArray[locationArray.length - 1] === "adauga";
            if (!isAddRoute) return;

            setItem(INITIAL_ITEM);
            setEditMode(true);
            setLoading(false);
        };

        setLoading(true);
        if(!ready) return;

        if (id === "") {
            initNewItemIfAddRoute();
            return;
        }

        APIGetCustomItem({id: id})
            .then((response) => {
                if (response.data.success) {
                    if (response.data.data !== null) {
                        const apiItem = response.data.data.item;
                        const item = {
                            ...apiItem,
                            is_active: apiItem.is_active === 1,
                        }
                        const itemGrade = apiItem.grade?.id;
                        const itemSubcategory = apiItem.subcategory !== null ? apiItem.subcategory.id : "";

                        setItem(item);
                        setSubject(apiItem.subject?.id);
                        setGrade(itemGrade);

                        if(item.subject !== null && itemGrade !== null) {
                            APIPostCustomSubcategories({subject: item.subject.id, grade: itemGrade})
                                .then((response) => {
                                    if (response.data.success) {
                                        setSubcategories(response.data.data.items);
                                        setSubcategory(itemSubcategory);
                                    } else {
                                        update("error", {show: true, code: "A002"});
                                    }
                                })
                                .catch(() => update("error", {show: true, code: "A001"}))
                        }
                    } else navigate("/pagina-nu-exista");
                } else update("error", {show: true, code: "A002"});
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, ready]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setErrorMessage("");
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const setSubjectHandler = (event: SelectChangeEvent) => {
        setGrade("");
        setSubcategory("");
        setSubject(event.target.value);
    }

    const setGradeHandler = (event: SelectChangeEvent) => {
        setSubcategory("");
        setGrade(event.target.value);

        APIPostCustomSubcategories({subject: subject, grade: event.target.value})
            .then((response) => {
                if (response.data.success) setSubcategories(response.data.data.items);
                else update("error", {show: true, code: "A002"});
            })
            .catch(() => update("error", {show: true, code: "A001"}));
    }

    const setSubcategoryHandler = (event: SelectChangeEvent) => {
        setSubcategory(event.target.value);
    }

    const imageHandler = (refName: string, file: string) => {
        let quill;
        if (refName === "q_data") quill = quillRefQData.current;
        if (refName === 'q_obs') quill = quillRefQObs.current;
        if (refName === 'q_1') quill = quillRefQ1.current;
        if (refName === 'q_2') quill = quillRefQ2.current;
        if (refName === 'q_3') quill = quillRefQ3.current;
        if (refName === 'q_4') quill = quillRefQ4.current;
        if (quill) {
            // @ts-ignore
            const range = quill.getSelection?.();
            if (range) {
                // @ts-ignore
                quill.insertEmbed(range.index, "image", file);
            } else {
                // @ts-ignore
                quill.insertEmbed(0, "image", file);
            }
        }
    };

    const handleResponseErrors = (err: any) => {
        if (err.response.status === 400) {
            setErrorMessage("Unul sau mai multe câmpuri nu sunt valide.");
            const errors = err.response.data.data;
            handleServerErrors(errors);
            update("loading", {show: false});
        } else {
            update("error", {show: true, code: "A001"});
        }
    }

    const onSubmit: SubmitHandler<IFormCustomItemsEdit> = (data) => {
        if (
            data.title !== item.title ||
            qData !== item.q_data ||
            qObs !== item.q_obs ||
            data.right_answer !== item.right_answer ||
            item.q_1 !== q1 ||
            item.q_2 !== q2 ||
            item.q_3 !== q3 ||
            item.q_4 !== q4 ||
            subject !== item.subject?.id ||
            grade !== item.grade?.id ||
            (item.subcategory !== null && subcategory !== item.subcategory?.id) ||
            item.subcategory === null
        ) {
            methods.clearErrors();
            setErrorMessage("");
            update("loading", {show: true, modal: true});
            if (id !== "") {
                APIUpdateCustomItem(id, {
                    title: data.title,
                    q_data: qData,
                    q_obs: qObs,
                    right_answer: data.right_answer,
                    q_1: q1,
                    q_2: q2,
                    q_3: q3,
                    q_4: q4,
                    subject: subject,
                    grade: grade,
                    subcategory: subcategory,
                })
                    .then((response) => {
                        if (response.data.success) {
                            setItem(response.data.data.item);
                            setEditMode(false);
                            navigate(`/itemi-proprii/${id}`, {replace: true});
                        } else update("error", {show: true, code: "A002"});
                    })
                    .catch(handleResponseErrors)
                    .finally(() => update("loading", {show: false}));
            } else {
                APIPostCustomItem({
                    title: data.title,
                    q_data: qData,
                    q_obs: qObs,
                    right_answer: data.right_answer,
                    q_1: q1,
                    q_2: q2,
                    q_3: q3,
                    q_4: q4,
                    subject: subject,
                    grade: grade,
                    subcategory: subcategory,
                })
                    .then((response) => {
                        if (response.data.success) {
                            navigate(`/itemi-proprii/${response.data.data.item.id}`, {replace: true});
                        } else update("error", {show: true, code: "A002"})
                    })
                    .catch(handleResponseErrors)
                    .finally(() => update("loading", {show: false}));
            }
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    };

    return (
        <Box component="main">
            {(loading) ? (
                <Loading show={true}/>
            ) : (
                <>
                    <PageTitle
                        back={true}
                        actions={
                            user.position === "teacher" && (
                                editMode ? (
                                    <GenericButton
                                        type="close"
                                        onClick={() => navigate(-1)}
                                    />
                                ) : (
                                    <GenericButton
                                        type="edit"
                                        onClick={() => navigate(`/itemi-proprii/${id}/editare`)}
                                    />
                                )
                            )
                        }
                    >
                        {id !== "" ? (
                            <>Item <strong style={{fontWeight: 400}}>{item.id}</strong></>
                        ) : (
                            <>Adaugă item</>
                        )}
                    </PageTitle>

                        <Box width="100%">
                            {!editMode ? (
                                <>
                                    <TestItemCard
                                        testMode={7}
                                        key={item.id}
                                        question={item}
                                        isCustomItemPreview={false}
                                        isCustomItemPreviewTest={true}
                                        difficulty={item.difficulty}
                                        isAnswerReport={false}
                                        isPrinting={false}
                                    />
                                </>

                            ) : (
                                <BoxContent>
                                <FormProvider {...methods}>
                                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                        <Grid container spacing={1.5} alignItems="center">
                                            <FormLabelBox title="Materie:"/>
                                            <FormFieldBox>
                                                <FormField
                                                    name="subject"
                                                    type="controlled-select"
                                                    label="Alege materia"
                                                    value={subject}
                                                    options={subjects.map((item: { name: string; id: string }) => {
                                                        return {
                                                            label: item.name,
                                                            value: item.id,
                                                            ...item,
                                                        };
                                                    })}
                                                    errorMessage={methods.formState.errors.subject?.message}
                                                    error={!!methods.formState.errors.subject?.message}
                                                    onChange={setSubjectHandler}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox title="Clasă:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="grade"
                                                    type="controlled-select"
                                                    label="Alege clasa"
                                                    disabled={!subject}
                                                    value={grade}
                                                    options={grades.map((item: { name: string; id: string }) => {
                                                        return {
                                                            label: item.name,
                                                            value: item.id,
                                                            ...item,
                                                        };
                                                    })}
                                                    errorMessage={methods.formState.errors.grade?.message}
                                                    error={!!methods.formState.errors.grade?.message}
                                                    onChange={setGradeHandler}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox title="Subcategorie:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="subcategory"
                                                    type="controlled-select"
                                                    label={subcategories.length === 0 && grade && subject ? "Nu există subcategorii pentru selecția facută" : "Alege subcategoria"}
                                                    multiple={false}
                                                    value={subcategory}
                                                    disabled={!grade || subcategories.length === 0}
                                                    options={subcategories.map(
                                                        (item: { name: string; id: string; [key: string]: any }) => {
                                                            return {
                                                                label: item.name,
                                                                value: item.id,
                                                                id: item.id,
                                                                name: item.name,
                                                            };
                                                        },
                                                    )}
                                                    errorMessage={methods.formState.errors.subcategory?.message}
                                                    error={!!methods.formState.errors.subcategory?.message}
                                                    onChange={setSubcategoryHandler}
                                                    disableClearable={true}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Răspuns corect:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="right_answer"
                                                    type="select"
                                                    defaultValue={item.right_answer}
                                                    label="Alege răspunsul corect"
                                                    options={[
                                                        {label: "a", value: "a"},
                                                        {label: "b", value: "b"},
                                                        {label: "c", value: "c"},
                                                        {label: "d", value: "d"},
                                                    ]}
                                                    errorMessage={methods.formState.errors.right_answer?.message}
                                                    error={!!methods.formState.errors.right_answer?.message}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Titlu:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="title"
                                                    type="text"
                                                    defaultValue={item.title}
                                                    label="Titlu"
                                                    errorMessage={methods.formState.errors.title?.message}
                                                    error={!!methods.formState.errors.title?.message}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Enunț:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQData}
                                                    name="q_data"
                                                    defaultValue={item.q_data}
                                                    imageHandler={imageHandler}
                                                    passText={setQData}
                                                    errorMessage={methods.formState.errors.q_data?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Cerință:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQObs}
                                                    name="q_obs"
                                                    defaultValue={item.q_obs}
                                                    imageHandler={imageHandler}
                                                    passText={setQObs}
                                                    errorMessage={methods.formState.errors.q_obs?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="a:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQ1}
                                                    name="q_1"
                                                    defaultValue={item.q_1}
                                                    imageHandler={imageHandler}
                                                    passText={setQ1}
                                                    errorMessage={methods.formState.errors.q_1?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="b:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQ2}
                                                    name="q_2"
                                                    defaultValue={item.q_2}
                                                    imageHandler={imageHandler}
                                                    passText={setQ2}
                                                    errorMessage={methods.formState.errors.q_2?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="c:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQ3}
                                                    name="q_3"
                                                    defaultValue={item.q_3}
                                                    imageHandler={imageHandler}
                                                    passText={setQ3}
                                                    errorMessage={methods.formState.errors.q_3?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="d:"/>
                                            <FormFieldBox>
                                                <CustomTextEditor
                                                    ref={quillRefQ4}
                                                    name="q_4"
                                                    defaultValue={item.q_4}
                                                    imageHandler={imageHandler}
                                                    passText={setQ4}
                                                    errorMessage={methods.formState.errors.q_4?.message}
                                                    clearErrors={methods.clearErrors}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox/>
                                            <FormFieldBox>
                                                <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                    {id !== "" ? "SALVEAZĂ DATELE" : "ADAUGĂ ITEM"}
                                                </CustomButton>
                                                <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                            </FormFieldBox>
                                        </Grid>
                                    </form>
                                </FormProvider>
                                </BoxContent>
                            )}

                        </Box>
                </>
            )}
        </Box>
    );
};

export default CustomTestItem;
